// Plain-text formatter for a program day / whole week. Used by the Share
// button on WeekView to hand a coach or athlete a copy-pasteable summary
// they can drop into SMS, WhatsApp, Notes, or the body of an email.
//
// Output is compact but human-readable — dashes for movements, indented
// details for prescriptions. No markdown, so it renders correctly
// everywhere including plain-text SMS.

import { formatDuration, DAY_NAMES, addDays, formatDate } from "@/lib/api";

const _weight = (mv) => {
  if (mv.weight == null || mv.weight === "") return null;
  return `${mv.weight}${mv.weight_unit ? ` ${mv.weight_unit}` : " lb"}`;
};

const _distance = (mv) => {
  if (mv.distance_meters == null || mv.distance_meters === "") return null;
  return `${mv.distance_meters} ${mv.distance_unit || "m"}`;
};

const _height = (mv) => {
  if (mv.height_inches == null || mv.height_inches === "") return null;
  return `↕ ${mv.height_inches} ${mv.height_unit || "in"}`;
};

function movementLine(mv) {
  const bits = [];
  const scheme = (mv.rep_scheme || mv.reps_scheme || []).length
    ? (mv.rep_scheme || mv.reps_scheme).join("-")
    : null;
  if (scheme) {
    bits.push(`${scheme} reps`);
  } else {
    if (mv.sets && mv.reps) bits.push(`${mv.sets}×${mv.reps}`);
    else if (mv.sets) bits.push(`${mv.sets} sets`);
    else if (mv.reps) bits.push(`${mv.reps} reps`);
  }
  const w = _weight(mv);          if (w) bits.push(w);
  if (mv.time_seconds) bits.push(formatDuration(mv.time_seconds));
  const d = _distance(mv);        if (d) bits.push(d);
  if (mv.calories) bits.push(`${mv.calories} cal`);
  const h = _height(mv);          if (h) bits.push(h);
  if (mv.rpe) bits.push(`RPE ${mv.rpe}`);
  const meta = bits.length ? ` — ${bits.join(" · ")}` : "";
  const superset = mv.superset_group_id ? "  [SUPERSET]" : "";
  const notes = mv.notes ? `\n     · ${mv.notes.trim().replace(/\n/g, " ")}` : "";
  return `  - ${mv.name}${meta}${superset}${notes}`;
}

function blockText(blk) {
  const type = (blk.type || "").toUpperCase();
  const title = blk.title ? ` — ${blk.title}` : "";
  const sc = blk.scheme_config || {};
  const schemeBits = [];
  if (sc.rounds) schemeBits.push(`${sc.rounds} rounds`);
  if (sc.time_cap_minutes) schemeBits.push(`${sc.time_cap_minutes} min cap`);
  if (sc.rep_scheme && sc.rep_scheme.length) schemeBits.push(sc.rep_scheme.join("-"));
  if (sc.emom_minutes) schemeBits.push(`${sc.emom_minutes} min`);
  if (sc.work_seconds || sc.rest_seconds) schemeBits.push(`${sc.work_seconds || 20}s work / ${sc.rest_seconds || 10}s rest`);
  const schemeLine = schemeBits.length ? ` (${schemeBits.join(" · ")})` : "";
  const header = `[${type}]${title}${schemeLine}`;
  const movements = (blk.movements || []).map(movementLine).join("\n");
  return movements ? `${header}\n${movements}` : header;
}

/**
 * Format a single day. Includes the date banner and each block.
 * Empty days render "REST" (if flagged) or "— no programming —".
 */
export function formatDayText(program, dayIdx) {
  if (!program) return "";
  const day = program.days?.[dayIdx];
  if (!day) return "";
  const date = addDays(program.week_start, dayIdx);
  const banner = `${DAY_NAMES[dayIdx]} · ${formatDate(date, { month: "short", day: "numeric", year: "numeric" })}`;
  if (day.is_rest_day) return `${banner}\nREST DAY`;
  const blocks = (day.blocks || []).map(blockText).join("\n\n");
  return blocks ? `${banner}\n\n${blocks}` : `${banner}\n— no programming —`;
}

/**
 * Format the whole week. Skips empty days entirely (keeps the message
 * short for SMS) but keeps REST DAY flags.
 */
export function formatWeekText(program, athleteName) {
  if (!program) return "";
  const header = [
    "HealthSpan Training",
    athleteName ? `Athlete: ${athleteName}` : null,
    `Week of ${formatDate(program.week_start, { month: "short", day: "numeric", year: "numeric" })}`,
  ].filter(Boolean).join("\n");
  const body = DAY_NAMES.map((_, idx) => {
    const day = program.days?.[idx];
    if (!day) return null;
    if (!day.is_rest_day && !(day.blocks || []).length) return null;
    return formatDayText(program, idx);
  }).filter(Boolean).join("\n\n───────────────\n\n");
  return `${header}\n\n${body}`;
}

// ────────────────────────────────────────────────────────────────
// Print-to-PDF variant — same content, styled HTML for a browser
// print dialog. Users pick "Save as PDF" as the destination.
// ────────────────────────────────────────────────────────────────

const _escape = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");

function _dayHtml(program, dayIdx) {
  const day = program.days?.[dayIdx];
  if (!day) return "";
  const date = addDays(program.week_start, dayIdx);
  const banner = `${DAY_NAMES[dayIdx]} · ${formatDate(date, { month: "short", day: "numeric", year: "numeric" })}`;
  if (day.is_rest_day) {
    return `<section class="day"><h2>${banner}</h2><p class="rest">REST DAY</p></section>`;
  }
  const blocks = (day.blocks || []).map((blk) => {
    const rawText = formatDayText({ ...program, days: [ { ...day, blocks: [blk] } ] }, 0)
      .split("\n").slice(2).join("\n");   // drop banner
    return `<pre class="block">${_escape(rawText)}</pre>`;
  }).join("");
  const body = blocks || `<p class="empty">— no programming —</p>`;
  return `<section class="day"><h2>${banner}</h2>${body}</section>`;
}

function _pdfShell(title, bodyHtml) {
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>${_escape(title)}</title>
<style>
  @page { size: Letter; margin: 0.6in; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color: #1B1B1B; line-height: 1.45; }
  header { border-bottom: 2px solid #2F4A3A; padding-bottom: 12px; margin-bottom: 18px; }
  header h1 { font-size: 22pt; margin: 0 0 4px 0; color: #2F4A3A; }
  header .meta { font-size: 10pt; color: #666; letter-spacing: 0.06em; text-transform: uppercase; }
  section.day { break-inside: avoid; margin-bottom: 22px; }
  section.day h2 { font-size: 14pt; margin: 0 0 8px 0; color: #2F4A3A; border-bottom: 1px solid #E8E4D8; padding-bottom: 4px; }
  pre.block { font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 10.5pt; white-space: pre-wrap; background: #F7F6F2; border-radius: 6px; padding: 10px 14px; margin: 8px 0; }
  p.rest { font-weight: bold; color: #B25C3E; letter-spacing: 0.1em; }
  p.empty { color: #888; font-style: italic; }
  footer { margin-top: 30px; font-size: 9pt; color: #888; border-top: 1px solid #E8E4D8; padding-top: 8px; }
  @media print { header h1 { break-after: avoid; } }
</style></head>
<body>${bodyHtml}
<footer>Generated by HealthSpan Training</footer>
<script>window.addEventListener("load", () => setTimeout(() => window.print(), 250));</script>
</body></html>`;
}

/** Open a new browser window with the day rendered as a print-ready page. */
export function openDayPdf(program, dayIdx, athleteName) {
  if (!program) return;
  const date = addDays(program.week_start, dayIdx);
  const title = `Training — ${DAY_NAMES[dayIdx]}, ${date}${athleteName ? ` · ${athleteName}` : ""}`;
  const header = `<header>
    <h1>${_escape(DAY_NAMES[dayIdx])} · ${_escape(formatDate(date, { month: "long", day: "numeric", year: "numeric" }))}</h1>
    <div class="meta">HealthSpan Training${athleteName ? ` · ${_escape(athleteName)}` : ""}</div>
  </header>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(_pdfShell(title, header + _dayHtml(program, dayIdx)));
  w.document.close();
}

/** Open a new browser window with the whole week rendered as a print-ready page. */
export function openWeekPdf(program, athleteName) {
  if (!program) return;
  const title = `Training — Week of ${program.week_start}${athleteName ? ` · ${athleteName}` : ""}`;
  const header = `<header>
    <h1>Week of ${_escape(formatDate(program.week_start, { month: "long", day: "numeric", year: "numeric" }))}</h1>
    <div class="meta">HealthSpan Training${athleteName ? ` · ${_escape(athleteName)}` : ""}</div>
  </header>`;
  const daysHtml = DAY_NAMES.map((_, idx) => {
    const day = program.days?.[idx];
    if (!day) return "";
    if (!day.is_rest_day && !(day.blocks || []).length) return "";
    return _dayHtml(program, idx);
  }).join("");
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(_pdfShell(title, header + daysHtml));
  w.document.close();
}
